import { useState } from "react";

import toast from "components/partials/toast/toast";
import { useRefineEmailContent } from "components/modals/refine-email-content-modal/use-refine-email-content";

import type { TouchpointVersionDTOAiFeedbackRating } from "generated/schemas";

import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import {
  hasTouchpointVersionFeedback,
  useSubmitTouchpointVersionFeedback,
} from "hooks/api/use-touchpoint-version-feedback";

import { getErrorMessage } from "utilities";

import AiContentFeedbackPanel from "./ai-content-feedback-panel";
import AiContentVoteBlock from "./ai-content-vote-block";
import {
  buildFeedbackComment,
  mapVoteToFeedbackType,
} from "./ai-content-feedback.helpers";
import type {
  AiContentFeedbackAction,
  AiContentPreviewArea,
  AiContentVote,
} from "./ai-content-feedback.types";

type AiContentFeedbackProps = {
  clientId: string;
  campaignId: string;
  touchpointId: string;
  versionId: string;
  aiFeedbackRating?: TouchpointVersionDTOAiFeedbackRating | null;
  onFeedbackRecorded?: (rating: TouchpointVersionDTOAiFeedbackRating) => void;
};

const AiContentFeedback = ({
  clientId,
  campaignId,
  touchpointId,
  versionId,
  aiFeedbackRating,
  onFeedbackRecorded,
}: AiContentFeedbackProps) => {
  const currentTouchpoint = useCurrentTouchpoint();

  const feedbackScope = {
    clientId,
    campaignId,
    touchpointId,
    versionId,
  };

  const submitFeedbackMutation = useSubmitTouchpointVersionFeedback(feedbackScope);
  const { refineEmail, isSubmitting: isRewriteSubmitting } = useRefineEmailContent({
    clientId,
    campaignId,
    touchpointId,
    versionId,
    currentTouchpoint,
  });

  const [selectedVote, setSelectedVote] = useState<AiContentVote | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState<AiContentPreviewArea[]>([]);
  const [feedback, setFeedback] = useState("");

  const isBusy = submitFeedbackMutation.isPending || isRewriteSubmitting;
  const isSubmitDisabled = feedback.trim().length === 0 || isBusy;
  const hasExistingFeedback = hasTouchpointVersionFeedback(aiFeedbackRating);

  function resetPanelState() {
    setIsPanelOpen(false);
    setSelectedVote(null);
    setSelectedAreas([]);
    setFeedback("");
  }

  function handleVoteSelect(vote: AiContentVote) {
    setSelectedVote(vote);
    setIsPanelOpen(true);
  }

  function handleAreaToggle(area: AiContentPreviewArea) {
    setSelectedAreas((currentAreas) =>
      currentAreas.includes(area)
        ? currentAreas.filter((currentArea) => currentArea !== area)
        : [...currentAreas, area],
    );
  }

  async function handleSubmit(action: AiContentFeedbackAction) {
    if (!selectedVote || feedback.trim().length === 0 || isBusy) {
      return;
    }

    const feedbackText = feedback.trim();
    const rating = mapVoteToFeedbackType(selectedVote);

    try {
      // Feedback is write-once on the version; skip re-POST when retrying rewrite.
      if (!hasExistingFeedback) {
        await submitFeedbackMutation.mutateAsync({
          type: rating,
          comment: buildFeedbackComment(feedback, selectedAreas),
        });
        onFeedbackRecorded?.(rating);
      }

      if (action === "rewrite") {
        const didRewrite = await refineEmail({
          emailAdditionalInstructions: feedbackText,
        });
        if (didRewrite) {
          resetPanelState();
        }
        return;
      }

      toast.success({
        data: { title: "Feedback sent successfully" },
      });
      resetPanelState();
    } catch (error) {
      toast.error({
        data: {
          title: "Failed to send feedback",
          message: getErrorMessage(error) || "Please try again.",
        },
        autoClose: false,
      });
    }
  }

  // Feedback already recorded: hide the vote block. Keep the panel mounted when open
  // so a failed rewrite can be retried without losing the form.
  if (hasExistingFeedback && !isPanelOpen) {
    return null;
  }

  return (
    <>
      {!hasExistingFeedback && (
        <AiContentVoteBlock selectedVote={selectedVote} onVoteSelect={handleVoteSelect} />
      )}
      {isPanelOpen && selectedVote && (
        <AiContentFeedbackPanel
          selectedAreas={selectedAreas}
          feedback={feedback}
          isSubmitDisabled={isSubmitDisabled}
          onAreaToggle={handleAreaToggle}
          onFeedbackChange={setFeedback}
          onClose={resetPanelState}
          onCancel={resetPanelState}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default AiContentFeedback;
